import {
  filter,
  groupBy,
  concat,
  cloneDeep,
  map,
  isEmpty,
  isString,
  memoize
} from "lodash"
// import moment from "moment"
import { getMoment } from "./MomentUtils"
import {
  AIR_HANDLER,
  AUTOMATED_TESTS,
  FAIL_COUNT_FIELD,
  FAMILY_FIELD,
  NAME_FIELD,
  NO_DATA_KEY,
  NOT_APPLICABLE_KEY,
  ROOT,
  SCORE_FIELD,
  STAND_ALONE,
  VAV_AIR_SYS
} from "src/components/buildingSummaryNewDesign/constants"
import { ASC, DESC } from "src/blitz/DataGridTableNewDesign/constants"
import store from "src/redux/Store"
const moment = getMoment()

// Optimized cache key generator - uses simple string concatenation instead of JSON.stringify
const generateResultsCacheKey = (analyticsResultDisplay) => {
  if (!analyticsResultDisplay || analyticsResultDisplay.length === 0)
    return "empty"
  // Join results with a delimiter - much faster than JSON.stringify
  return analyticsResultDisplay.map((item) => item?.result || "N").join("|")
}

// Helper function for score calculation (memoized for performance)
const calculateScoreInternal = (analyticsResultDisplay) => {
  if (!analyticsResultDisplay || analyticsResultDisplay.length === 0) return 0

  // Optimized: single pass through array counting passes and valid results
  // Valid results are G (Green), Y (Yellow), R (Red) - excludes "No data" and "Not Applicable"
  let passCount = 0
  let validCount = 0

  for (let i = 0; i < analyticsResultDisplay.length; i++) {
    const result = analyticsResultDisplay[i]?.result

    // Count valid results (green, yellow, red)
    if (result === "G" || result === "Y" || result === "R") {
      validCount++
      if (result === "G") {
        passCount++
      }
    }
  }

  // If no valid results, return 0
  if (validCount === 0) return 0

  return (passCount / validCount) * 100
}

// Memoized version to prevent recalculation of same data
export const calculateScore = memoize(
  calculateScoreInternal,
  generateResultsCacheKey
)

// Helper function for fail count calculation (memoized for performance)
const calculateFailCountInternal = (analyticsResultDisplay) => {
  if (!analyticsResultDisplay || analyticsResultDisplay.length === 0) return 0

  // Optimized: single pass through array counting failures
  let failCount = 0
  const length = analyticsResultDisplay.length

  for (let i = 0; i < length; i++) {
    if (analyticsResultDisplay[i]?.result === "R") {
      failCount++
    }
  }

  return failCount
}

// Memoized version to prevent recalculation of same data
export const calculateFailCount = memoize(
  calculateFailCountInternal,
  generateResultsCacheKey
)

// Calculates pass percentage by accumulating ALL individual test results from piSummary
// across every day in the date range for an equipment.
// Formula: Total G / Total (G + Y + R) * 100
// Returns undefined when no valid (G/Y/R) results exist across all tests and days.
const calculateCountsFromPiSummaries = (
  analyticsItems: any[]
): { g: number; y: number; r: number } => {
  const counts = { g: 0, y: 0, r: 0 }
  if (!analyticsItems || analyticsItems.length === 0) return counts
  for (const item of analyticsItems) {
    if (!item?.piSummary) continue
    const suppressedTests: Set<string> = new Set(
      Array.isArray(item.sdAts) ? item.sdAts : []
    )
    try {
      const piSummary = JSON.parse(item.piSummary)
      for (const [testName, result] of Object.entries(piSummary)) {
        if (suppressedTests.has(testName)) continue
        if (result === "G") counts.g++
        else if (result === "Y") counts.y++
        else if (result === "R") counts.r++
      }
    } catch {}
  }
  return counts
}

export const calculateScoreFromPiSummaries = (
  analyticsItems: any[]
): number | undefined => {
  if (!analyticsItems || analyticsItems.length === 0) return undefined

  let totalG = 0
  let totalValid = 0

  for (const item of analyticsItems) {
    if (!item?.piSummary) continue
    const suppressedTests: Set<string> = new Set(
      Array.isArray(item.sdAts) ? item.sdAts : []
    )
    try {
      const piSummary = JSON.parse(item.piSummary)
      for (const [testName, result] of Object.entries(piSummary)) {
        if (suppressedTests.has(testName)) continue
        if (result === "G" || result === "Y" || result === "R") {
          totalValid++
          if (result === "G") totalG++
        }
      }
    } catch {}
  }

  if (totalValid === 0) return undefined
  return Math.round((totalG / totalValid) * 100)
}

export const hasValidTestResults = (analyticsResultDisplay) => {
  if (!analyticsResultDisplay || analyticsResultDisplay.length === 0)
    return false

  for (let i = 0; i < analyticsResultDisplay.length; i++) {
    const result = analyticsResultDisplay[i]?.result
    if (result === "G" || result === "Y" || result === "R") {
      return true
    }
  }

  return false
}

// Pure recursive method to calculate parent score from combined children analytics data
// Aggregates all analytics data from descendants and calculates one percentage
export const calculateParentScore = (parent: any): number => {
  if (!parent?.sub || parent.sub.length === 0) return 0

  // Helper function to recursively collect all analytics data from all descendants
  const collectAllAnalyticsData = (node: any): any[] => {
    if (!node?.sub || node.sub.length === 0) {
      // Leaf node - return its analytics data
      return node.analyticsResultDisplay || []
    }

    // Parent node - collect data from all children recursively
    let allData: any[] = []
    for (const child of node.sub) {
      // Include ALL children (including automatedTestRow nodes which contain actual test results)
      allData = allData.concat(collectAllAnalyticsData(child))
    }
    return allData
  }

  // Collect all analytics data from all descendants
  const allAnalyticsData = collectAllAnalyticsData(parent)

  if (allAnalyticsData.length === 0) return 0

  // Calculate score from combined data: (pass count / valid count) * 100
  // Valid results are G (Green), Y (Yellow), R (Red) - excludes "No data" and "Not Applicable"
  const validResults = allAnalyticsData.filter(
    (r: any) => r.result === "G" || r.result === "Y" || r.result === "R"
  )

  // If no valid results, return 0
  if (validResults.length === 0) return 0

  const passCount = validResults.filter((r: any) => r.result === "G").length
  return Math.round((passCount / validResults.length) * 100)
}

// Helper function to recursively add calculated parent scores to entire data structure
// This handles arbitrary depth hierarchies
export const addCalculatedParentScores = (data: any): any => {
  if (!Array.isArray(data)) return data

  const processNode = (node: any): any => {
    const processed = {
      ...node,
      calculatedParentScore: !node.automatedTestRow
        ? calculateParentScore(node)
        : null
    }

    // Recursively process all levels of sub-children
    if (node.sub && Array.isArray(node.sub)) {
      processed.sub = node.sub.map((child: any) => processNode(child))
    }

    return processed
  }

  return data.map((row: any) => processNode(row))
}

export const unGroupSystemHeirarchy = (arr: any, result = []) => {
  arr.forEach((obj: any) => {
    const { sub, ...rest } = obj
    result.push(rest)
    if (sub) {
      unGroupSystemHeirarchy(sub, result)
    }
  })
  return result
}

export const findChildItems = (item, value, key) => {
  if (item[key] === value) {
    return true
  }
  if (item?.sub && item.sub?.length > 0) {
    return item.sub.find((s) => findChildItems(s, value, key))
  }
  return false
}

export const validParentId = (el: any = {}) =>
  el?.parentId &&
  el?.parentId !== null &&
  el?.parentId !== "" &&
  el?.parentId !== "NA"

export const frameEquipmentsTree = (
  data: any,
  twoWeekRange: any,
  automatedResults: any,
  Standalone: any,
  translatedAutomatedTests: any,
  translatedAutomatedTestRow: any,
  analyticConfigEquipmentTypes = []
): any => {
  if (data?.length === 0) return []

  // Optimize: Create a Map for faster equipment lookup
  const automatedResultsMap = new Map()
  automatedResults.forEach((item) => {
    if (!automatedResultsMap.has(item.equipmentId)) {
      automatedResultsMap.set(item.equipmentId, [])
    }
    automatedResultsMap.get(item.equipmentId).push(item)
  })

  const analyticResultItems = (el: any) => automatedResultsMap.get(el) || []

  const nullParentId = (el: any) =>
    el.parentId === "" ||
    el.parentId === "NA" ||
    !Boolean(el.parentId) ||
    el.parentId === "(null)"

  const totalEquipmentsList = data
    .filter((equipment) => {
      if (nullParentId(equipment)) {
        return true
      }
      return !data.find(
        (eq) =>
          isString(equipment?.parentId) &&
          equipment?.parentId?.split("#")?.includes(eq?.id)
      )
    })
    .map((el: any) => {
      return {
        ...el,
        equipmentType: el?.isParent ? ROOT : "StandAloneC"
      }
    })

  const EquipmentsWithParentIdList = data
    .filter((equipment) => {
      if (validParentId(equipment)) {
        return !!data.find(
          (eq) =>
            isString(equipment?.parentId) &&
            equipment?.parentId?.split("#")?.includes(eq?.id)
        )
      }
      return false
    })
    .map((el: any) => {
      return {
        ...el,
        parentId: el?.parentId?.split("#")
      }
    })
  const equipmentsList = totalEquipmentsList?.filter(
    (eq) => eq.equipmentType === ROOT
  )
  const standAlone = totalEquipmentsList?.filter(
    (eq) => eq.equipmentType === "StandAloneC"
  )

  const standAloneGroup = groupBy(standAlone, FAMILY_FIELD)
  const standAloneP = Object.entries(standAloneGroup).map((s) => {
    const [parentName, data] = [...s]
    const dataWithAnalytics = data.map((obj) => {
      const rawItems = analyticResultItems(obj?.id)
      const analyticsResultDisplay = includeAnalyticsStatus(
        twoWeekRange,
        rawItems,
        false,
        obj?.type,
        analyticConfigEquipmentTypes
      )
      return {
        ...obj,
        analyticsResultDisplay,
        piSummaryScore: calculateScoreFromPiSummaries(rawItems)
      }
    })
    return {
      name: `${Standalone} ${parentName}`,
      family: `${Standalone} ${parentName}`,
      isParent: true,
      equipmentType: STAND_ALONE,
      analyticsResult: {
        items: []
      },
      analyticsResultDisplay: includeAnalyticsStatus(twoWeekRange, []),
      sub: dataWithAnalytics
    }
  })

  const automatedTestRowGenerator = (parent: any) => {
    return [
      {
        analyticsResult: { items: analyticResultItems(parent?.id) },
        family: parent?.family,
        id: parent?.id + " - " + translatedAutomatedTestRow,
        atrID: parent?.id,
        parentId: parent?.id,
        isParent: false,
        name: translatedAutomatedTests + " - " + parent?.name,
        type: parent?.type,
        automatedTestRow: true
      }
    ]
  }

  const formEquipmentChildren = (parents: any, rootLevel: boolean) => {
    for (let i = 0; i < parents.length; i++) {
      const parent = parents[i]
      const parentId = Boolean(parent?.automatedTestRow)
        ? parent?.parentId
        : parent?.id
      const childrens = filter(EquipmentsWithParentIdList, (el: any) =>
        el.parentId.includes(parent.id)
      )
      const isParent = childrens?.length > 0
      parent.analyticsResultDisplay = includeAnalyticsStatus(
        twoWeekRange,
        analyticResultItems(parentId),
        isParent,
        parent?.type,
        analyticConfigEquipmentTypes
      )
      parent.piSummaryScore = calculateScoreFromPiSummaries(
        analyticResultItems(parentId)
      )
      parent.piSummaryCounts = calculateCountsFromPiSummaries(
        analyticResultItems(parentId)
      )
      if (isParent) {
        if (!rootLevel) {
          parent.equipmentType = "P"
        }
        parent.isParent = true
        const automatedTestRow = automatedTestRowGenerator(parent)
        parent.sub = [...automatedTestRow, ...childrens]
        parents[i] = parent
        formEquipmentChildren(parent.sub, false)
        // Children are now fully processed. Compute parent score and counts by
        // accumulating raw G/Y/R counts from all real children + own (if own has data).
        // This gives the same result as if every individual test were counted once.
        const realChildren = parent.sub.filter((c: any) => !c.automatedTestRow)
        const ownCounts = parent.piSummaryCounts || { g: 0, y: 0, r: 0 }
        const ownHasData = ownCounts.g + ownCounts.y + ownCounts.r > 0
        const totalCounts = realChildren.reduce(
          (acc: { g: number; y: number; r: number }, c: any) => {
            const counts = c.piSummaryCounts || { g: 0, y: 0, r: 0 }
            return {
              g: acc.g + counts.g,
              y: acc.y + counts.y,
              r: acc.r + counts.r
            }
          },
          {
            g: ownHasData ? ownCounts.g : 0,
            y: ownHasData ? ownCounts.y : 0,
            r: ownHasData ? ownCounts.r : 0
          }
        )
        const totalValid = totalCounts.g + totalCounts.y + totalCounts.r
        if (totalValid > 0) {
          parent.piSummaryCounts = totalCounts
          parent.piSummaryScore = (totalCounts.g / totalValid) * 100
        }
      }
    }
  }
  formEquipmentChildren(equipmentsList, true)

  return concat(equipmentsList, standAloneP || [])
}

const getAllIndexes = (arr, val) => {
  const indexes = []
  let i = -1
  while ((i = arr.indexOf(val, i + 1)) != -1) {
    indexes.push(i)
  }
  return indexes
}

export const includeAnalyticsStatus = (
  twoWeekRange = [],
  analyticsResult = [],
  isParent = false,
  type = "",
  analyticConfigEquipmentTypes = []
) => {
  // Optimize: Use map instead of cloneDeep for better performance
  const isATResultApplicable = analyticConfigEquipmentTypes?.includes(type)

  // Create a lookup map for faster access
  const analyticsMap = new Map(analyticsResult?.map((ar) => [ar?.testDate, ar]))

  const weekRange = twoWeekRange?.map((day: any) => {
    const thisDayAR = analyticsMap.get(day?.date)
    let piData = NO_DATA_KEY

    if (!isATResultApplicable) {
      piData = NOT_APPLICABLE_KEY
    }
    if (isParent) {
      if (thisDayAR?.rollUpResult) {
        const res = JSON.parse(thisDayAR?.rollUpResult)?.res
        if (res) {
          piData = res
        }
      }
    } else {
      piData = isATResultApplicable
        ? thisDayAR?.pi || NO_DATA_KEY
        : NOT_APPLICABLE_KEY
    }

    return {
      ...day,
      result: piData || NO_DATA_KEY
    }
  })
  return weekRange
}

export const mapAnalyticResult = (twoWeekRange, analyticName, results) => {
  if (isEmpty(results)) return twoWeekRange
  const weekRange = twoWeekRange.map((day) => {
    const resultKey = `${day.date}###${analyticName}`
    const performanceIndicator = results[resultKey]
    const resultObject = performanceIndicator
      ? {
          performanceIndicator,
          result: performanceIndicator
        }
      : {}
    return {
      ...day,
      ...resultObject
    }
  })
  return weekRange
}

export const sortFunction = (a, b, sortOrder, multiple = false) => {
  const sortOrderType = sortOrder === DESC ? -1 : 1
  if (multiple) {
    const nameA =
      a.name === null || a.name === undefined ? "" : a.name.toString()
    const nameB =
      b.name === null || b.name === undefined ? "" : b.name.toString()
    const familyA =
      a.family === null || a.family === undefined ? "" : a.family.toString()
    const familyB =
      b.family === null || b.family === undefined ? "" : b.family.toString()
    const familyComparison = familyA.localeCompare(familyB, "en", {
      numeric: true
    })
    if (familyComparison !== 0) {
      return sortOrderType * familyComparison
    }
    return (
      sortOrderType *
      nameA.localeCompare(nameB, "en", {
        numeric: true
      })
    )
  } else {
    const nameA =
      a.name === null || a.name === undefined ? "" : a.name.toString()
    const nameB =
      b.name === null || b.name === undefined ? "" : b.name.toString()
    return (
      nameA.localeCompare(nameB, "en", {
        numeric: true
      }) * sortOrderType
    )
  }
}

export const sortAnalyticsBuildingSummaryPage = (
  data: any,
  currentSortField: string,
  sortOrder: string,
  language?: string
) => {
  if (currentSortField === NAME_FIELD) {
    const equipmentsList = data?.filter((eq: any) => eq.equipmentType === ROOT)
    const standAlone = data?.filter(
      (eq: any) => eq.equipmentType === STAND_ALONE
    )

    const selectedLanguage = store?.getState().userData.selectedLanguage ?? "en"
    const sortEquipments = (arr: any, sortOrder: string, multiple = false) => {
      return [...arr]
        .sort((a, b) => sortFunction(a, b, sortOrder, multiple))
        .map((obj) => {
          if (obj.sub) {
            const automatedObjects = obj.sub.filter((c) =>
              selectedLanguage === "en"
                ? c.name?.includes(AUTOMATED_TESTS)
                : c?.automatedTestRow
            )
            let airHandler = []
            let remaining = obj.sub.filter((c) =>
              selectedLanguage === "en"
                ? !c.name?.includes(AUTOMATED_TESTS)
                : !c?.automatedTestRow
            )
            if (obj?.family === VAV_AIR_SYS) {
              airHandler = sortEquipments(
                obj.sub.filter((c) => c.family.includes(AIR_HANDLER)),
                sortOrder
              )
              remaining = remaining.filter(
                (c) => !c.family.includes(AIR_HANDLER)
              )
            }
            if (language !== "en") {
              obj.sub = sortEquipments(remaining, sortOrder)
              if (automatedObjects.length > 0) {
                obj.sub = automatedObjects.concat(obj.sub)
              }
              if (obj?.family === VAV_AIR_SYS && airHandler.length > 0) {
                obj.sub =
                  sortOrder === ASC
                    ? obj.sub.concat(airHandler)
                    : airHandler.concat(obj.sub)
              }
            } else {
              obj.sub = sortEquipments(remaining, sortOrder)
              if (automatedObjects.length > 0) {
                if (obj?.family === VAV_AIR_SYS && airHandler.length > 0) {
                  obj.sub =
                    sortOrder === ASC
                      ? automatedObjects.concat(airHandler, obj.sub)
                      : automatedObjects.concat(obj.sub, airHandler)
                } else {
                  obj.sub = automatedObjects.concat(obj.sub)
                }
              }
            }
          }
          return obj
        })
    }

    const finalSortedEquipments = sortEquipments(equipmentsList, sortOrder)
    const finalSortedStandalone = sortEquipments(standAlone, sortOrder)
    const final =
      sortOrder === ASC
        ? concat(finalSortedEquipments, finalSortedStandalone)
        : concat(finalSortedStandalone, finalSortedEquipments)
    return final
  }

  if (currentSortField === FAMILY_FIELD) {
    const filteredEquipments = unGroupSystemHeirarchy(data)
      ?.filter((obj) => !obj?.name?.includes(AUTOMATED_TESTS))
      ?.filter((obj) => !(obj?.equipmentType === STAND_ALONE))
    const finalArray = filteredEquipments.sort((a, b) =>
      sortFunction(a, b, sortOrder, true)
    )
    return finalArray
  }

  if (currentSortField === SCORE_FIELD) {
    const filteredEquipments = unGroupSystemHeirarchy(data)
      ?.filter((obj) => !obj?.name?.includes(AUTOMATED_TESTS))
      ?.filter((obj) => !(obj?.equipmentType === STAND_ALONE))

    // Use accumulated piSummary score (all tests × all days) when available,
    const equipmentsWithScores = filteredEquipments.map((equipment) => ({
      ...equipment,
      _calculatedScore: equipment.piSummaryScore ?? 0
    }))

    const finalArray = equipmentsWithScores.sort((a, b) => {
      if (sortOrder === ASC) {
        return a._calculatedScore - b._calculatedScore
      } else {
        return b._calculatedScore - a._calculatedScore
      }
    })

    // Remove the temporary score property
    return finalArray.map(({ _calculatedScore, ...equipment }) => equipment)
  }

  if (currentSortField === FAIL_COUNT_FIELD) {
    const filteredEquipments = unGroupSystemHeirarchy(data)
      ?.filter((obj) => !obj?.name?.includes(AUTOMATED_TESTS))
      ?.filter((obj) => !(obj?.equipmentType === STAND_ALONE))

    // Pre-calculate fail counts to avoid multiple calculations during sort
    const equipmentsWithFailCounts = filteredEquipments.map((equipment) => ({
      ...equipment,
      _calculatedFailCount: calculateFailCount(equipment.analyticsResultDisplay)
    }))

    const finalArray = equipmentsWithFailCounts.sort((a, b) => {
      if (sortOrder === ASC) {
        return a._calculatedFailCount - b._calculatedFailCount
      } else {
        return b._calculatedFailCount - a._calculatedFailCount
      }
    })

    // Remove the temporary fail count property
    return finalArray.map(({ _calculatedFailCount, ...equipment }) => equipment)
  }
}
const TWO_WEEK_LENGTH = 14
export const getDaysFromEndDate = (endDate: any) => {
  const weekRange = []
  for (let i = 0; i < TWO_WEEK_LENGTH; i++) {
    const d = moment(endDate).locale("en").subtract(i, "days")
    weekRange.unshift({
      name: d.format("ddd"),
      title: d.format("dddd, MMMM D, YYYY"),
      date: d.format("YYYY-MM-DD"),
      result: NO_DATA_KEY
    })
  }
  return weekRange
}

// New function to generate date range based on user-selected start and end dates
export const getDaysFromDateRange = (startDate: any, endDate: any) => {
  const dateRange = []
  const start = moment(startDate).locale("en")
  const end = moment(endDate).locale("en")

  // Calculate the difference in days
  const daysDiff = end.diff(start, "days") + 1 // +1 to include both start and end dates

  for (let i = 0; i < daysDiff; i++) {
    const d = moment(start).add(i, "days")
    dateRange.push({
      name: d.format("ddd"),
      title: d.format("dddd, MMMM D, YYYY"),
      date: d.format("YYYY-MM-DD"),
      result: NO_DATA_KEY
    })
  }

  return dateRange
}

export const filterEquipmentByDeviceIds = (
  equipments: any[],
  deviceIdSet: Set<string>
): any[] => {
  return equipments.filter((eq) => {
    if (deviceIdSet.has(eq.deviceId)) return true
    if (eq.isParent) {
      return equipments.some(
        (child) =>
          child.parentId?.split("#")?.includes(eq.id) &&
          deviceIdSet.has(child.deviceId)
      )
    }
    return false
  })
}

